/**
 * Domain logic for @-mentioning in brief comments: which entities are taggable
 * (ALL project audiences + project members) and how to read agent
 * mentions back out of stored Tiptap content. The mention editor extension
 * itself is generic and lives in `entities/project-comment`.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AgentAvatar, getPersonaAgentId } from '@/entities/agent';
import { personasByIdsQuery } from '@/entities/persona';
import { projectQuery } from '@/entities/project';
import type { MentionOption } from '@/entities/project-comment';
import { usePeopleMentionOptions } from '@/entities/project-member';
import { useCurrentUserId } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useTranslation } from 'react-i18next';

export function extractAgentMentions(content: Record<string, unknown>): string[] {
  const ids = new Set<string>();
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return;
    const n = node as Record<string, unknown>;
    const attrs = n.attrs as { id?: string; type?: string } | undefined;
    if (n.type === 'mention' && attrs?.type === 'agent' && attrs.id) {
      ids.add(attrs.id);
    }
    for (const child of (n.content as unknown[] | undefined) ?? []) {
      walk(child);
    }
  };
  walk(content);
  return [...ids];
}

/**
 * Taggable entities for a brief: ALL project audiences + project
 * members. (The mentioned-in-text filter applies only to the automatic
 * review, not to who can be tagged.)
 */
export function useBriefMentionOptions(args: {
  projectId: string;
  platformProjectId?: string | null;
}): MentionOption[] {
  const { t } = useTranslation('chats');
  const workspaceId = useCurrentWorkspaceId();
  const { data: project } = useQuery(projectQuery(args.projectId));
  // Fetch the audience personas BY ID — project audiences can be scoped
  // personas that don't appear in the workspace personas list, and the
  // photo/title live on the persona record.
  const audienceIds = useMemo(() => (project?.audiences ?? []).map((a) => a.id), [project]);
  const { data: personas } = useQuery(personasByIdsQuery(workspaceId, audienceIds));
  const people = usePeopleMentionOptions(workspaceId, args.platformProjectId);
  const currentUserId = useCurrentUserId();

  return useMemo(() => {
    const personaById = new Map((personas ?? []).map((p) => [p.id, p]));
    const audienceOptions: MentionOption[] = (project?.audiences ?? []).map((a) => {
      const persona = personaById.get(a.id);
      const agentId = getPersonaAgentId(a.id);
      // Display convention for audiences across the app is the segment name
      // (a.name IS the persona's segmentName); personaTitle is a long tagline.
      const label = a.name;
      return {
        id: agentId,
        label,
        type: 'agent' as const,
        avatarSlot: (
          <AgentAvatar
            agentId={agentId}
            agentTitle={label}
            variant="small"
            avatar={
              persona?.photoAssetId ? { type: 'assetId', assetId: persona.photoAssetId } : undefined
            }
          />
        ),
      };
    });

    return [
      ...audienceOptions,
      ...people.map((p) =>
        p.type === 'user' && p.id === currentUserId
          ? { ...p, labelSuffix: t('briefAgentReview.agentMentions.youSuffix') }
          : p,
      ),
    ];
  }, [project, personas, people, currentUserId, t]);
}
